// Maps weighted resiliency data to 3D visual properties.
// Height  <- Financial (35%) + Impact/Innovation (30%)
// Mass    <- total real-estate footprint (acres)
// Glow    <- Prestige (20%) + Brand Awareness (5%)
// Stability <- Sustainability (10%): steady when high, flickering when low

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const norm = (v, min, max) => clamp((v - min) / (max - min), 0, 1);

// Null-safe accessors: some lower-ranked schools have gaps in the source data.
// Fall back to the overall resiliency rating (or a neutral mid value) so the
// 3D node always renders instead of collapsing to NaN.
const fin = (inst) => inst.financial ?? inst.resiliency_rating ?? 75;
const pres = (inst) => inst.prestige ?? inst.resiliency_rating ?? 70;
const brnd = (inst) => inst.brand ?? inst.resiliency_rating ?? 80;
const sust = (inst) => inst.sustainability ?? 70;

export function impactValue(inst) {
  // Fall back to financial (then resiliency) when impact is missing.
  return inst.impact ?? fin(inst);
}

export function combinedFinInno(inst) {
  const imp = impactValue(inst);
  return (fin(inst) * 35 + imp * 30) / 65;
}

export function glowScore(inst) {
  return (pres(inst) * 20 + brnd(inst) * 5) / 25;
}

export function nodeHeight(inst) {
  const t = norm(combinedFinInno(inst), 83, 98.5);
  return 1.6 + t * 4.6;
}

export function nodeWidth(inst) {
  const lg = Math.log(inst.footprint_acres);
  const t = norm(lg, 3.4, 9.2);
  return 0.5 + t * 0.95;
}

export function emissiveBase(inst) {
  const t = norm(glowScore(inst), 80, 97);
  return 0.35 + t * 1.55;
}

// 0 = perfectly steady, 1 = maximum flicker (low adaptability)
export function flickerAmount(inst) {
  return norm(80 - sust(inst), 0, 18);
}

export const HEAT_CATEGORIES = [
  { key: "composite", label: "Composite" },
  { key: "financial", label: "Financial" },
  { key: "impact", label: "Impact" },
  { key: "prestige", label: "Prestige" },
  { key: "sustainability", label: "Sustainability" },
  { key: "brand", label: "Brand" },
];

// Compute {min, max} per category across the dataset (ignoring nulls).
export function categoryRanges(institutions) {
  const keys = ["financial", "impact", "prestige", "sustainability", "brand"];
  const ranges = {};
  for (const k of keys) {
    const vals = institutions
      .map((i) => i[k])
      .filter((v) => v != null);
    ranges[k] = { min: Math.min(...vals), max: Math.max(...vals) };
  }
  return ranges;
}

// Two-segment heatmap: muted slate (low) -> amber (mid) -> rich gold (high).
export function heatColor(t) {
  t = clamp(t, 0, 1);
  const stops = [
    [174, 182, 194],
    [228, 197, 94],
    [192, 137, 31],
  ];
  const seg = t < 0.5 ? 0 : 1;
  const local = t < 0.5 ? t / 0.5 : (t - 0.5) / 0.5;
  const a = stops[seg];
  const b = stops[seg + 1];
  const c = a.map((v, i) => Math.round(v + (b[i] - v) * local));
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
}

export const HIGH_PRESTIGE_THRESHOLD = 92;
export function isHighPrestige(inst) {
  return pres(inst) >= HIGH_PRESTIGE_THRESHOLD;
}

// Ground-ring radius (world units) representing a school's real-estate footprint.
export function footprintRadius(inst) {
  const lg = Math.log(inst.footprint_acres);
  const t = clamp((lg - 3.4) / (9.2 - 3.4), 0, 1);
  return 1.2 + t * 5.2;
}
